// @ts-nocheck
import { useState } from "react";
import { Button, DatePicker, Input, Select } from "../components/admin-ui";
import {
  DownloadOutlined, FilterOutlined, SearchOutlined,
} from "../components/admin-icons";
import type { ColumnsType } from "../components/admin-ui";
import type { ActivityLog } from "@kawkaw/shared";
import { activityMetaText, activityVerbLabel, cancelReasonLabel, orderStatusLabel, payMethodLabel, riderStatusLabel } from "@kawkaw/shared";
import { useTranslation } from "react-i18next";
import { DataTable } from "../components/DataTable";
import { AdminShell } from "../layouts/AdminShell";
import { tokens } from "../styles/tokens";
const { color: T } = tokens;
import { fetchAllActivity, useActivityLog } from "../api/hooks";
import { formatDateTimeWithSeconds, formatMytStamp } from "../utils/formatLocaleDate";

const { RangePicker } = DatePicker;

/* ── Action pill colours ─────────────────────────── */
const ACTION_STYLE: Record<string, { bg: string; color: string }> = {
  create:    { bg: T.successBg, color: T.successText },
  update:    { bg: T.surfaceBlue, color: T.infoDark },
  delete:    { bg: T.dangerBg, color: T.dangerText },
  login:     { bg: T.purpleBg, color: T.purpleText },
  logout:    { bg: T.surfaceSection, color: T.textBody },
  refund:    { bg: T.brandLight, color: T.brandWarm },
  reset_pin: { bg: T.warningBg, color: T.brandAmberText },
};


/* ── Readable labels (zh); other languages get a humanised code ── */
const ENTITY_ZH: Record<string, string> = {
  bundle: "套餐", customer: "顾客", delivery_provider: "配送服务商", feature_flag: "功能开关", holiday: "休息日",
  kopitiam: "门店信息", menu_category: "菜单分类", menu_item: "菜品", menu_option_choice: "选项内容",
  menu_option_group: "选项组", order: "订单", order_item: "订单菜品", order_stall_status: "档口订单状态",
  payment_method_config: "支付方式", promo_banner: "横幅", refund: "退款", sms_provider: "短信服务商",
  staff: "员工", stall: "档口", store: "门店", voucher: "优惠券", settings: "系统设置", stall_order: "档口订单",
  operations: "营业设置", delivery_settings: "配送设置", legal_document: "法律内容", payment_method: "支付方式",
  option_choice: "选项内容", option_group: "选项组", banner: "横幅", stall_application: "档口申请",
};
const VERB_ZH: Record<string, string> = {
  create: "新增", update: "修改", delete: "删除", reorder: "调整顺序", accept: "接单", cancelled: "取消",
  admin_cancel: "后台取消", assign_rider: "指派骑手", lalamove_dispatch: "Lalamove 派送", lalamove_booked: "已叫 Lalamove，正在找骑手", lalamove_failed: "叫 Lalamove 失败", lalamove_quote: "Lalamove 报价",
  lalamove_resubmit: "Lalamove 重新提交", mark_paid: "标记已付款", walkin_create: "新增现场订单", reset_password: "重置密码",
  reset_pin: "重置 PIN", register_webhook: "注册 Webhook", test: "测试", toggle_open: "开/关档口", toggle: "开/关门店",
  distribute: "派发", submit_template: "提交短信模板", logo_update: "更换标志", item_reject: "拒绝菜品",
  refund: "退款", rider_status: "骑手状态", approve: "批准", reject: "拒绝", approved: "批准", rejected: "拒绝", preparing: "制作中", ready: "已备好",
  out_for_delivery: "配送中", in_transit: "运送中", delivered: "已送达", completed: "已完成", login: "登录", logout: "登出",
};
const humanise = (c: string) => c.replace(/[._]/g, " ");
const isZh = (lng: string) => lng.startsWith("zh");
export function actionLabel(action: string, lng: string): string {
  const [ent, ...rest] = action.split(".");
  const verb = rest.join(".");
  const shared = activityVerbLabel(verb, lng);
  if (!isZh(lng)) return (shared ?? humanise(action)).toUpperCase();
  if (!verb) return VERB_ZH[ent] ?? humanise(ent);
  const v = shared ?? VERB_ZH[verb] ?? humanise(verb);
  // "订单" + "Lalamove …" must not run together
  return `${ENTITY_ZH[ent] ?? humanise(ent)}${/^[A-Za-z0-9]/.test(v) ? " " : ""}${v}`;
}
const entityLabel = (e: string, lng: string) => (isZh(lng) ? ENTITY_ZH[e] ?? humanise(e) : humanise(e));
const metaText = (v: Record<string, unknown>, lng: string): string => activityMetaText(v, lng);

function ActionPill({ action, lng }: { action: string; lng: string }) {
  const verb = action.split(".").pop() ?? action;
  const s = ACTION_STYLE[action] ?? ACTION_STYLE[verb] ?? { bg: T.surfaceSection, color: T.textBody };
  return (
    <span className="admin-activity-action-pill admin-data-badge" style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
      background: s.bg, color: s.color,
    }}>
      {actionLabel(action, lng)}
    </span>
  );
}

/* ── Screen ──────────────────────────────────────── */
export function ActivityLogScreen() {
  const { t, i18n } = useTranslation();
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState("");
  const [action, setAction]       = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  const { data, isLoading } = useActivityLog({
    page, limit: 10,
    q: search || undefined,
    action,
    from: dateRange?.[0],
    to: dateRange?.[1],
  });

  const columns: ColumnsType<ActivityLog> = [
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: T.textMuted }}>{t("activity.colStaff").toUpperCase()}</span>,
      key: "staff", width: 160,
      render: (_, row) => {
        const r = row as unknown as { staff?: { name: string } };
        const name = r.staff?.name ?? t("common.system");
        return <span style={{ fontSize: 14, fontWeight: 600, color: T.textDark }}>{name}</span>;
      },
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: T.textMuted }}>{t("activity.colAction").toUpperCase()}</span>,
      dataIndex: "action", key: "action", width: 130,
      render: (v: string) => <ActionPill action={v} lng={i18n.language} />,
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: T.textMuted }}>{t("activity.colEntity").toUpperCase()}</span>,
      key: "entity", width: 180,
      render: (_, row) => {
        const r = row as unknown as { entity_type?: string; entity_id?: string; meta?: Record<string, unknown> | null };
        // a readable reference only — never the internal id
        const m = r.meta ?? {};
        const friendly = [m.orderCode, m.item_name, m.stall_name, m.name].find((x) => typeof x === "string" && x) as string | undefined;
        if (!r.entity_type) return <span style={{ color: T.textMuted }}>—</span>;
        return (
          <span style={{ fontSize: 13 }}>
            <span style={{ color: T.textDark, fontWeight: 600 }}>{entityLabel(r.entity_type, i18n.language)}</span>{" "}
            {friendly && (
              <span title={r.entity_id} style={{ marginLeft: 6, padding: "1px 6px", background: T.surfaceSection, borderRadius: 4, fontSize: 11, color: T.textBody }}>
                {friendly}
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: T.textMuted }}>{t("activity.colDetails").toUpperCase()}</span>,
      dataIndex: "meta", key: "desc",
      render: (v: Record<string, unknown> | null) =>
        v && Object.keys(v).length > 0
          ? <span style={{ fontSize: 12, color: T.textBody, wordBreak: "break-all" }}>{metaText(v, i18n.language)}</span>
          : <span style={{ color: T.textMuted }}>—</span>,
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: T.textMuted }}>{t("activity.colIp").toUpperCase()}</span>,
      dataIndex: "ip", key: "ip", width: 140,
      render: (v: string | null) =>
        v
          ? <span style={{ padding: "2px 8px", background: T.surfaceSection, borderRadius: 6, fontSize: 12, fontFamily: "monospace", color: T.textBody }}>{v}</span>
          : <span style={{ color: T.textMuted }}>—</span>,
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: T.textMuted }}>{t("activity.colTime").toUpperCase()}</span>,
      dataIndex: "created_at", key: "time", width: 168,
      render: (v: string) => (
        <span style={{ fontSize: 13, color: T.textBody }}>
          {formatDateTimeWithSeconds(v, i18n.language)}
        </span>
      ),
      sorter: true,
    },
  ];

  return (
    <AdminShell>
      <div className="admin-activity-page admin-list-page">
        <div className="admin-activity-page-inner">
      {/* Header */}
      <div className="admin-activity-toolbar">
        <div>
          <h1 className="admin-page-title">{t("activity.title")}</h1>
          <p className="admin-activity-subtitle">
            {t("activity.eventsRecorded", { count: data?.meta.total ?? 0 })}
          </p>
        </div>
        <Button icon={<DownloadOutlined />}
          onClick={async () => {
            const rows = await fetchAllActivity({ q: search || undefined, action, from: dateRange?.[0], to: dateRange?.[1] });
            const lng = i18n.language;
            const header = [t("activity.colTime"), t("activity.colStaff"), t("activity.colAction"), t("activity.colEntity"), t("activity.colDetails"), t("activity.colIp")];
            const csvRows = rows.map((r) => {
              const m = ((r as unknown as { meta?: Record<string, unknown> | null }).meta ?? {}) as Record<string, unknown>;
              const friendly = [m.orderCode, m.item_name, m.stall_name, m.name].find((x) => typeof x === "string" && x) as string | undefined;
              return [
              formatMytStamp(r.created_at),
              r.staff?.name ?? t("common.system"),
              actionLabel(r.action, lng),
              `${entityLabel(r.entity_type, lng)}${friendly ? ` ${friendly}` : ""}`,
              metaText(m, lng),
              r.ip ?? "",
            ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",");
            });
            const csv = [header.join(","), ...csvRows].join("\n");
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `activity-log-${formatMytStamp(new Date()).slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="admin-activity-export"
          style={{ height: 44, borderRadius: 10, fontWeight: 700, fontSize: 14, borderColor: T.border, color: T.textBody, paddingInline: 20 }}>
          {t("activity.exportCsv")}
        </Button>
      </div>

      {/* Filter bar */}
      <div className="admin-activity-filters admin-txn-filter-panel">
        <Input
          prefix={<SearchOutlined style={{ color: T.textMuted }} />}
          placeholder={t("activity.searchPlaceholder")}
          value={search}
          onChange={e => setSearch(e.target.value)}
          allowClear
          className="admin-activity-search"
          style={{ height: 44, borderRadius: 10, fontSize: 14, maxWidth: 280, width: "100%" }}
        />
        <Select
          placeholder={
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <FilterOutlined style={{ color: T.textMuted }} />
              <span style={{ color: T.textMuted }}>{t("activity.allActions")}</span>
            </span>
          }
          allowClear
          value={action}
          onChange={v => { setAction(v as string | undefined); setPage(1); }}
          className="admin-activity-action-filter"
          style={{ height: 44, borderRadius: 10, maxWidth: 170, width: "100%" }}
          options={["create","update","delete","refund","reset_pin"].map(a => ({
            value: a,
            label: <ActionPill action={a} lng={i18n.language} />,
          }))}
        />
        <RangePicker
          showTime
          onChange={(_, s) => {
            setDateRange(s[0] && s[1] ? [s[0], s[1]] : null);
            setPage(1);
          }}
          className="admin-activity-date-filter"
          style={{ height: 44, borderRadius: 10, fontSize: 14, maxWidth: 260, width: "100%" }}
        />
        {(search || action || dateRange) && (
          <button
            type="button"
            onClick={() => { setSearch(""); setAction(undefined); setDateRange(null); setPage(1); }}
            className="admin-activity-clear"
            style={{ border: "none", background: "transparent", color: T.brand, fontSize: 13, fontWeight: 700, cursor: "pointer", padding: "0 4px" }}
          >
            {t("activity.clearFilters")}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="admin-activity-table-panel admin-data-table-panel">
        <div className="admin-activity-table-heading">
          <div>{t("activity.title")}</div>
          <div>{data?.meta.total ?? 0}</div>
        </div>
        <DataTable<ActivityLog>
          columns={columns}
          dataSource={data?.data ?? []}
          loading={isLoading}
          sticky={false}
          pagination={{
            current: page,
            total: data?.meta.total ?? 0,
            pageSize: 10,
            onChange: (p) => setPage(p),
          }}
        />
      </div>
        </div>
      </div>
    </AdminShell>
  );
}
